// @ts-nocheck

import React, { useEffect, useState } from 'react';
import { Product } from '../../types/product';
import { formatNumbers } from '../../utils/convert-to-arabic-price';
import RIYALIcon from '../../assets/riyal.svg?react';
import { getFrequentlyBoughtTogetherProducts } from '../../api/getFbt';
import {
  ENTERED_FROM_FBT,
  ENTERED_FROM_FBT_MODAL,
} from '../../constants/tracking';
import {
  setTrackingSource,
  setDirectTrackingFlag,
} from '../../utils/tracking-session';
import { safeTrack } from '../../utils/analytics';
import { addToCartTracking } from '../../utils/cart-tracking';

interface FrequentlyBoughtTogetherProps {
  currency?: string;
  locale?: 'ar-SA' | 'en-US';
  primaryColor?: string;
  store: string;
  provider: string;
  product?: string;
  customer?: string;
  hideTitle?: boolean;
  skipInitialLoading?: boolean;
  preloadedProducts?: Product[];
  onModalClose?: () => void;
  type?: 'modal' | 'page';
  language?: string;
  fptTitle?: string;
  fptTitleEn?: string;
}

const FrequentlyBoughtTogether: React.FC<FrequentlyBoughtTogetherProps> = ({
  currency = 'SAR',
  locale = 'ar-SA',
  primaryColor = '#000000',
  store,
  provider,
  product,
  customer,
  hideTitle = false,
  skipInitialLoading = false,
  preloadedProducts,
  onModalClose,
  type = 'page',
  language = 'ar',
  fptTitle = 'عادة ما يتم شراؤها معًا',
  fptTitleEn = 'Frequently Bought Together',
}) => {
  const [products, setProducts] = useState<Product[]>([]);
  const [selectedProducts, setSelectedProducts] = useState<Set<number>>(
    new Set(),
  );
  const [isLoading, setIsLoading] = useState(!skipInitialLoading);
  // State for storing product variants for Zid products
  const [productVariants, setProductVariants] = useState<Record<number, any[]>>(
    {},
  );
  // State for storing selected variant for each product
  const [selectedVariants, setSelectedVariants] = useState<Record<number, any>>(
    {},
  );

  // Mock data for testing
  const getMockProducts = (): Product[] => [
    {
      id: 1,
      name: {
        ar: 'هاتف ذكي آيفون 15 برو ماكس',
        en: 'iPhone 15 Pro Max Smartphone',
      },
      url: window.location.href, // Current page URL since this is the main product
      mainImage:
        'https://images.unsplash.com/photo-1695048133142-1a20484d2569?w=300&h=300&fit=crop',
      price: 4999,
      discountedPrice: 4599,
      isAvailable: true,
      productId: 1,
      categories: [{ name: { ar: 'هواتف ذكية', en: 'Smartphones' } }],
      currency: currency,
      description: {
        ar: 'أحدث هاتف ذكي من آبل مع شاشة 6.7 بوصة وكاميرا 48 ميجابكسل',
        en: 'Latest smartphone from Apple with 6.7-inch display and 48MP camera',
      },
      is_available: true,
      media: [],
      parent_id: 0,
      promotion_title: { ar: 'خصم خاص', en: 'Special Discount' },
      quantity: 1,
      sku: 'IPHONE-15-PM',
      status: 'active',
      store: {
        description: 'متجر الإلكترونيات',
        id: 1,
        name: 'متجر التقنية',
        route_key: 'tech-store',
        store_id: store,
      },
      store_id: 1,
      subtitle: { ar: 'أداء استثنائي', en: 'Exceptional Performance' },
      tags: [{ name: { ar: 'آيفون', en: 'iphone' } }],
      type: 'product',
      unlimited_quantity: false,
      weight: 0.3,
      weight_type: 'kg',
      badges: { ar: 'الأكثر مبيعاً', icon: '🔥' },
    },
    {
      id: 2,
      name: {
        ar: 'حافظة جلدية فاخرة للآيفون 15',
        en: 'Premium Leather Case iPhone 15',
      },
      url: 'https://example.com/iphone-15-case',
      mainImage:
        'https://images.unsplash.com/photo-1556656793-08538906a9f8?w=300&h=300&fit=crop',
      price: 189,
      discountedPrice: 149,
      isAvailable: true,
      productId: 2,
      categories: [
        { name: { ar: 'اكسسوارات الهاتف', en: 'Phone Accessories' } },
      ],
      currency: currency,
      description: {
        ar: 'حافظة جلدية أصلية عالية الجودة مصممة خصيصاً للآيفون 15',
        en: 'Premium genuine leather case specifically designed for iPhone 15',
      },
      is_available: true,
      media: [],
      parent_id: 0,
      promotion_title: { ar: '', en: '' },
      quantity: 1,
      sku: 'IP15-CASE-LEATHER',
      status: 'active',
      store: {
        description: 'متجر الإلكترونيات',
        id: 1,
        name: 'متجر التقنية',
        route_key: 'tech-store',
        store_id: store,
      },
      store_id: 1,
      subtitle: { ar: 'حماية أنيقة وفاخرة', en: 'Elegant Premium Protection' },
      tags: [{ name: { ar: 'حافظة آيفون', en: 'iphone case' } }],
      type: 'product',
      unlimited_quantity: false,
      weight: 0.08,
      weight_type: 'kg',
      badges: { ar: 'جديد', icon: '✨' },
    },
    {
      id: 3,
      name: {
        ar: 'شاحن سريع MagSafe للآيفون',
        en: 'MagSafe Fast Charger iPhone',
      },
      url: 'https://example.com/magsafe-charger',
      mainImage:
        'https://images.unsplash.com/photo-1586953208448-b95a79798f07?w=300&h=300&fit=crop',
      price: 279,
      isAvailable: true,
      productId: 3,
      categories: [
        { name: { ar: 'اكسسوارات الشحن', en: 'Charging Accessories' } },
      ],
      currency: currency,
      description: {
        ar: 'شاحن لاسلكي مغناطيسي أصلي من آبل يدعم الشحن السريع',
        en: 'Original Apple MagSafe wireless charger with fast charging support',
      },
      is_available: true,
      media: [],
      parent_id: 0,
      promotion_title: { ar: '', en: '' },
      quantity: 1,
      sku: 'MAGSAFE-CHR-15W',
      status: 'active',
      store: {
        description: 'متجر الإلكترونيات',
        id: 1,
        name: 'متجر التقنية',
        route_key: 'tech-store',
        store_id: store,
      },
      store_id: 1,
      subtitle: { ar: 'شحن مغناطيسي سريع', en: 'Fast Magnetic Charging' },
      tags: [{ name: { ar: 'شاحن آيفون', en: 'iphone charger' } }],
      type: 'product',
      unlimited_quantity: false,
      weight: 0.15,
      weight_type: 'kg',
      badges: { ar: '', icon: '' },
    },
    {
      id: 4,
      name: {
        ar: 'سماعات AirPods Pro اللاسلكية',
        en: 'AirPods Pro Wireless Earbuds',
      },
      url: 'https://example.com/airpods-pro',
      mainImage:
        'https://images.unsplash.com/photo-1606318801954-d46d46d3360a?w=300&h=300&fit=crop',
      price: 899,
      isAvailable: false, // Out of stock product for testing
      productId: 4,
      categories: [{ name: { ar: 'سماعات', en: 'Headphones' } }],
      currency: currency,
      description: {
        ar: 'سماعات أصلية من آبل مع تقنية إلغاء الضوضاء النشطة',
        en: 'Original Apple earbuds with active noise cancellation technology',
      },
      is_available: false,
      media: [],
      parent_id: 0,
      promotion_title: { ar: '', en: '' },
      quantity: 0,
      sku: 'AIRPODS-PRO-2',
      status: 'out_of_stock',
      store: {
        description: 'متجر الإلكترونيات',
        id: 1,
        name: 'متجر التقنية',
        route_key: 'tech-store',
        store_id: store,
      },
      store_id: 1,
      subtitle: { ar: 'صوت عالي الجودة', en: 'High-Quality Audio' },
      tags: [{ name: { ar: 'سماعات آبل', en: 'apple earbuds' } }],
      type: 'product',
      unlimited_quantity: false,
      weight: 0.05,
      weight_type: 'kg',
      badges: { ar: '', icon: '' },
    },
  ];

  // Mock variants data for testing Zid variant functionality
  const getMockVariants = (): Record<number, any[]> => ({
    1: [
      {
        id: 'variant-1-gold',
        name: 'هاتف ذكي آيفون 15 برو ماكس - ذهبي',
        price: 4999,
        sale_price: 4599,
        formatted_price: '4999.00 ر.س',
        formatted_sale_price: '4599.00 ر.س',
        attributes: [
          {
            name: 'النوع',
            value: 'ذهبي',
          },
        ],
      },
      {
        id: 'variant-1-silver',
        name: 'هاتف ذكي آيفون 15 برو ماكس - فضي',
        price: 4999,
        sale_price: 4699,
        formatted_price: '4999.00 ر.س',
        formatted_sale_price: '4699.00 ر.س',
        attributes: [
          {
            name: 'النوع',
            value: 'فضي',
          },
        ],
      },
    ],
    2: [
      {
        id: 'variant-2-black',
        name: 'حافظة جلدية فاخرة للآيفون 15 - أسود',
        price: 189,
        sale_price: 149,
        formatted_price: '189.00 ر.س',
        formatted_sale_price: '149.00 ر.س',
        attributes: [
          {
            name: 'النوع',
            value: 'أسود',
          },
        ],
      },
      {
        id: 'variant-2-brown',
        name: 'حافظة جلدية فاخرة للآيفون 15 - بني',
        price: 189,
        sale_price: 149,
        formatted_price: '189.00 ر.س',
        formatted_sale_price: '149.00 ر.س',
        attributes: [
          {
            name: 'النوع',
            value: 'بني',
          },
        ],
      },
    ],
    3: [
      {
        id: 'variant-3-white',
        name: 'شاحن سريع MagSafe للآيفون - أبيض',
        price: 279,
        formatted_price: '279.00 ر.س',
        attributes: [
          {
            name: 'النوع',
            value: 'أبيض',
          },
        ],
      },
      {
        id: 'variant-3-black',
        name: 'شاحن سريع MagSafe للآيفون - أسود',
        price: 299,
        formatted_price: '299.00 ر.س',
        attributes: [
          {
            name: 'النوع',
            value: 'أسود',
          },
        ],
      },
    ],
    // Product 4 (AirPods) has no variants intentionally to test both scenarios
  });

  useEffect(() => {
    const fetchProducts = async () => {
      setIsLoading(true);
      try {
        // If preloaded products are provided, use them instead of making API call
        if (preloadedProducts && preloadedProducts.length > 0) {
          setProducts(preloadedProducts);
          // Only select available products by default, excluding first product if hideTitle is true
          const productsToSelect = hideTitle
            ? preloadedProducts.slice(1).filter((p) => p.isAvailable)
            : preloadedProducts.filter((p) => p.isAvailable);
          setSelectedProducts(new Set(productsToSelect.map((p) => p.id)));

          // Fetch variants for Zid products even with preloaded products
          if (
            provider === 'zid' &&
            window.zid &&
            preloadedProducts.length > 0
          ) {
            try {
              const variantPromises = preloadedProducts.map(async (prod) => {
                let productDetails;
                try {
                  const has_vitrin = window.vitrin == true;
                  if (has_vitrin) {
                    productDetails = await window.zid.products.get(
                      String(prod.productId),
                    );
                  } else {
                    productDetails = await window.zid.store.product.fetch(
                      String(prod.productId),
                    );
                  }
                  return {
                    productId: prod.id,
                    variants: has_vitrin
                      ? productDetails.variants
                      : productDetails.data.product.variants || [],
                  };
                } catch (error) {
                  console.error(
                    `Error fetching variants for product ${prod.productId}:`,
                    error,
                  );
                  return {
                    productId: prod.id,
                    variants: [],
                  };
                }
              });

              const variantResults = await Promise.all(variantPromises);
              const variantsMap: Record<number, any[]> = {};
              const selectedVariantsMap: Record<number, any> = {};

              variantResults.forEach(({ productId, variants }) => {
                if (variants.length > 0) {
                  variantsMap[productId] = variants;
                  // Select the first variant by default
                  selectedVariantsMap[productId] = variants[0];
                }
              });

              setProductVariants(variantsMap);
              setSelectedVariants(selectedVariantsMap);
            } catch (error) {
              console.error('Error fetching product variants:', error);
            }
          }

          setIsLoading(false);
          return;
        }

        // Use mock data based on environment
        const useMockData = false;

        if (useMockData) {
          // Simulate API delay
          await new Promise((resolve) => setTimeout(resolve, 1000));
          const mockProducts = getMockProducts();
          setProducts(mockProducts);
          // Only select available products by default, excluding first product if hideTitle is true
          const productsToSelect = hideTitle
            ? mockProducts.slice(1).filter((p) => p.isAvailable)
            : mockProducts.filter((p) => p.isAvailable);
          setSelectedProducts(new Set(productsToSelect.map((p) => p.id)));

          // Add mock variants for Zid provider testing
          if (provider === 'zid') {
            const mockVariants = getMockVariants();
            setProductVariants(mockVariants);

            // Select first variant for each product that has variants
            const mockSelectedVariants: Record<number, any> = {};
            Object.entries(mockVariants).forEach(([productId, variants]) => {
              if (variants.length > 0) {
                mockSelectedVariants[parseInt(productId)] = variants[0];
              }
            });
            setSelectedVariants(mockSelectedVariants);
          }
        } else {
          const products = await getFrequentlyBoughtTogetherProducts({
            storeId: store,
            storeType: provider,
            productId: product || undefined,
            currency,
          });
          setProducts(products);
          // Only select available products by default, excluding first product if hideTitle is true
          const productsToSelect = hideTitle
            ? products.slice(1).filter((p) => p.isAvailable)
            : products.filter((p) => p.isAvailable);
          setSelectedProducts(new Set(productsToSelect.map((p) => p.id)));

          // Fetch variants for Zid products in parallel
          if (provider === 'zid' && window.zid && products.length > 0) {
            try {
              const variantPromises = products.map(async (prod) => {
                let productDetails;
                try {
                  const has_vitrin = window.vitrin == true;
                  if (has_vitrin) {
                    productDetails = await window.zid.products.get(
                      String(prod.productId),
                    );
                  } else {
                    productDetails = await window.zid.store.product.fetch(
                      String(prod.productId),
                    );
                  }
                  return {
                    productId: prod.id,
                    variants: has_vitrin
                      ? productDetails.variants
                      : productDetails.data.product.variants || [],
                  };
                } catch (error) {
                  console.error(
                    `Error fetching variants for product ${prod.productId}:`,
                    error,
                  );
                  return {
                    productId: prod.id,
                    variants: [],
                  };
                }
              });

              const variantResults = await Promise.all(variantPromises);
              const variantsMap: Record<number, any[]> = {};
              const selectedVariantsMap: Record<number, any> = {};

              variantResults.forEach(({ productId, variants }) => {
                if (variants.length > 0) {
                  variantsMap[productId] = variants;
                  // Select the first variant by default
                  selectedVariantsMap[productId] = variants[0];
                }
              });

              setProductVariants(variantsMap);
              setSelectedVariants(selectedVariantsMap);
            } catch (error) {
              console.error('Error fetching product variants:', error);
            }
          }
        }
      } catch (error) {
        console.error('Error fetching FBT products:', error);
        // Fallback to mock data on error
        setProducts([]);
        setSelectedProducts(new Set());
      } finally {
        setIsLoading(false);
      }
    };

    if (preloadedProducts || (store && provider)) {
      fetchProducts();
    }
  }, [store, provider, product, currency, hideTitle, preloadedProducts]);

  // Handle product selection
  const toggleProductSelection = (productId: number) => {
    // Find the product to check if it's available
    const product = products.find((p) => p.id === productId);

    // Don't allow toggling unavailable products
    if (!product || !product.isAvailable) {
      return;
    }

    const newSelected = new Set(selectedProducts);
    if (newSelected.has(productId)) {
      newSelected.delete(productId);
    } else {
      newSelected.add(productId);
    }
    setSelectedProducts(newSelected);
  };

  // Handle variant selection for a product
  const handleVariantSelection = (productId: number, variant: any) => {
    setSelectedVariants((prev) => ({
      ...prev,
      [productId]: variant,
    }));
  };

  // Calculate total price using discounted price if available (only for selected products)
  const calculateTotal = () => {
    return products.reduce((total, product) => {
      if (selectedProducts.has(product.id)) {
        const price = product.discountedPrice || product.price;
        return total + price;
      }
      return total;
    }, 0);
  };

  const calculateOriginalTotal = () => {
    return products.reduce((total, product) => {
      if (selectedProducts.has(product.id)) {
        return total + product.price;
      }
      return total;
    }, 0);
  };

  const total = calculateTotal();
  const originalTotal = calculateOriginalTotal();
  const hasDiscount = total < originalTotal;
  const selectedProductsArray = products.filter((p) =>
    selectedProducts.has(p.id),
  );

  const onProductClick = async (
    product: Product,
    isCurrentProduct: boolean,
  ) => {
    // Don't allow clicking on the current product
    if (isCurrentProduct) {
      return;
    }

    setTrackingSource(
      type === 'modal' ? ENTERED_FROM_FBT_MODAL : ENTERED_FROM_FBT,
      product.productId.toString(),
    );

    safeTrack('product_clicked', {
      storeId: store,
      provider,
      customerId: customer,
      product: product.productId,
      productName: product.name.ar,
      type:
        type === 'modal'
          ? 'frequently_bought_together_modal'
          : 'frequently_bought_together',
    });

    window.open(
      language === 'ar' ? product.urlTrans?.ar : product.urlTrans?.en,
      '_self',
    );
  };

  const quickAddToCart = async (products: Product[], quantity: number) => {
    for (let i = 0; i < products.length; i++) {
      const product = products[i];
      safeTrack('product_added_to_cart', {
        storeId: store,
        provider,
        customerId: customer,
        product: product.productId,
        productName: product.name.ar,
        type: type === 'modal' ? 'fbt_modal' : 'fbt',
        reached_from:
          type === 'modal'
            ? 'frequently_bought_together_modal'
            : 'frequently_bought_together',
      });
      setDirectTrackingFlag(); // Set flag BEFORE API call to prevent ProductView duplicate
      addToCartTracking(
        product.name.ar,
        type === 'modal'
          ? 'frequently_bought_together_modal'
          : 'frequently_bought_together',
      );
      await window.salla?.api.cart.quickAdd(product.productId, quantity);

      // Add delay after adding product, except for the last one
      if (i < products.length - 1) {
        await new Promise((resolve) => setTimeout(resolve, 100)); // 120ms delay
      }
    }
  };

  const quickAddToZidCart = async (products: Product[]) => {
    if (!window.zid) return;

    for (const product of products) {
      const hasVariants =
        productVariants[product.id] && productVariants[product.id].length > 0;
      const selectedVariant = selectedVariants[product.id];

      if (hasVariants && !selectedVariant) {
        console.error(`No variant selected for product ${product.productId}`);
        continue;
      }

      setDirectTrackingFlag(); // Set flag BEFORE API call to prevent ProductView duplicate
      addToCartTracking(
        product.name.ar,
        type === 'modal'
          ? 'frequently_bought_together_modal'
          : 'frequently_bought_together',
      );

      try {
        const has_vitrin = window.vitrin == true;
        const product_id = hasVariants ? selectedVariant.id : product.productId;
        const addtoCartResponse = has_vitrin
          ? await window.zid.cart.addProduct({
              product_id: product_id,
              quantity: 1,
            })
          : await window.zid.store.cart.addProduct({
              productId: product_id,
              quantity: 1,
            });

        if (addtoCartResponse.item || addtoCartResponse.status === 'success') {
          safeTrack('product_added_to_cart', {
            storeId: store,
            provider,
            customerId: customer,
            product: product.productId,
            productName: product.name.ar,
            type:
              type === 'modal'
                ? 'frequently_bought_together_modal'
                : 'frequently_bought_together',
          });
        }
      } catch (error) {
        console.error('Error adding product to cart:', error);
      }
    }
  };

  const quickAddToWoocommerceCart = async (
    products: Product[],
    quantity: number,
  ) => {
    products.forEach(async (product) => {
      setDirectTrackingFlag(); // Set flag BEFORE API call to prevent ProductView duplicate
      addToCartTracking(
        product.name.ar,
        type === 'modal'
          ? 'frequently_bought_together_modal'
          : 'frequently_bought_together',
      );
      try {
        const response = await fetch('/?wc-ajax=add_to_cart', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          body: `product_id=${product.productId}&quantity=${quantity}`,
        });

        const data = await response.json();

        if (data.error) {
          console.error('Error adding to cart:', data.message);
        } else {
          safeTrack('product_added_to_cart', {
            storeId: store,
            provider,
            customerId: customer,
            product: product.productId,
            productName: product.name.ar,
            type:
              type === 'modal'
                ? 'frequently_bought_together_modal'
                : 'frequently_bought_together',
          });
          console.log('Product added to cart:', data);

          // Trigger WooCommerce's `added_to_cart` event
          document.dispatchEvent(
            new CustomEvent('added_to_cart', { detail: product.productId }),
          );

          // Fetch updated cart fragments and update UI
          const cartFragmentResponse = await fetch(
            '/?wc-ajax=get_refreshed_fragments',
          );
          const cartFragmentData = await cartFragmentResponse.json();

          if (cartFragmentData && cartFragmentData.fragments) {
            for (const [selector, html] of Object.entries(
              cartFragmentData.fragments,
            )) {
              const element = document.querySelector(selector);
              if (element) {
                element.innerHTML = html as string; // Replace the old fragment with the new one
              }
            }
          }
        }
      } catch (error) {
        console.error('Request failed:', error);
      }
    });
  };

  const onAddAllToCart = (products: Product[]) => {
    console.log('products', products);
    provider === 'salla'
      ? quickAddToCart(products, 1)
      : provider === 'zid'
      ? quickAddToZidCart(products)
      : quickAddToWoocommerceCart(products, 1);
    onModalClose?.();
  };

  if (isLoading && hideTitle) {
    return (
      <div className="frequently-bought-together">
        <div className="frequently-bought-together-loading">
          <span className="loader-alfinder-fpt"></span>
        </div>
      </div>
    );
  }

  if (products.length === 0 || products.length === 1 || isLoading) {
    return null;
  }

  return (
    <div
      className="frequently-bought-together"
      style={
        {
          margin: hideTitle ? '0' : '20px 0',
          boxShadow: hideTitle ? 'none' : '0 2px 8px rgba(0, 0, 0, 0.1)',
          border: hideTitle ? 'none' : '1px solid #e5e7eb',
          '--fbt-primary-color': primaryColor,
          '--fbt-primary-color-light': `${primaryColor}0d`, // 5% opacity
        } as React.CSSProperties
      }
    >
      {!hideTitle && (
        <h3 className="frequently-bought-together-title">
          {language === 'ar' ? fptTitle : fptTitleEn}
        </h3>
      )}

      <div className="frequently-bought-together-container">
        <div className="frequently-bought-together-products">
          {products.map((product, index) => {
            const isCurrentProduct = index === 0; // First product is the current product

            // Skip the first product if hideTitle is true (modal usage)
            if (hideTitle && isCurrentProduct) {
              return null;
            }

            return (
              <React.Fragment key={product.id}>
                <div
                  className={`frequently-bought-together-product ${
                    !product.isAvailable ? 'out-of-stock' : ''
                  } ${isCurrentProduct ? 'current-product' : ''}`}
                >
                  <div className="frequently-bought-together-product-checkbox">
                    <input
                      type="checkbox"
                      id={`product-${product.id}`}
                      checked={selectedProducts.has(product.id)}
                      disabled={!product.isAvailable}
                      onChange={() => toggleProductSelection(product.id)}
                    />
                    <label htmlFor={`product-${product.id}`}></label>
                  </div>

                  <div
                    className="frequently-bought-together-product-image"
                    onClick={() => onProductClick(product, isCurrentProduct)}
                    style={{
                      cursor: isCurrentProduct ? 'default' : 'pointer',
                    }}
                  >
                    <img
                      src={product.mainImage}
                      alt={
                        language === 'ar'
                          ? product.name.ar
                          : product.name.en || ''
                      }
                    />
                  </div>

                  <div className="frequently-bought-together-product-info">
                    <h4
                      className="frequently-bought-together-product-name"
                      onClick={() => onProductClick(product, isCurrentProduct)}
                      style={{
                        cursor: isCurrentProduct ? 'default' : 'pointer',
                      }}
                    >
                      {isCurrentProduct && (
                        <span className="current-product-label">
                          {language === 'ar' ? 'هذا المنتج:' : 'This product:'}{' '}
                        </span>
                      )}
                      {language === 'ar'
                        ? product.name.ar
                        : product.name.en || ''}
                    </h4>

                    <div className="frequently-bought-together-product-price">
                      {!product.isAvailable ? (
                        <div className="out-of-stock-text">
                          <span>
                            {language === 'ar' ? 'غير متوفر' : 'Out of Stock'}
                          </span>
                        </div>
                      ) : product.discountedPrice ? (
                        <div className="price-container">
                          <div className="current-price">
                            <span className="price-value">
                              {formatNumbers(
                                product.discountedPrice,
                                language === 'ar' ? 'ar-SA' : 'en-US',
                                currency,
                              )}
                            </span>
                            {currency === 'SAR' && (
                              <RIYALIcon className="riyal-icon" />
                            )}
                          </div>
                          <div className="original-price">
                            <span className="price-value">
                              {formatNumbers(
                                product.price,
                                language === 'ar' ? 'ar-SA' : 'en-US',
                                currency,
                              )}
                            </span>
                            {currency === 'SAR' && (
                              <RIYALIcon className="riyal-icon" />
                            )}
                          </div>
                        </div>
                      ) : (
                        <div className="current-price">
                          <span className="price-value">
                            {formatNumbers(
                              product.price,
                              language === 'ar' ? 'ar-SA' : 'en-US',
                              currency,
                            )}
                          </span>
                          {currency === 'SAR' && (
                            <RIYALIcon className="riyal-icon" />
                          )}
                        </div>
                      )}
                    </div>

                    {/* Variant selection for Zid products */}
                    {provider === 'zid' &&
                      productVariants[product.id] &&
                      productVariants[product.id].length > 0 && (
                        <div className="frequently-bought-together-variants">
                          <select
                            className="variant-select"
                            value={selectedVariants[product.id]?.id || ''}
                            onChange={(e) => {
                              const selectedVariant = productVariants[
                                product.id
                              ].find(
                                (variant) => variant.id === e.target.value,
                              );
                              if (selectedVariant) {
                                handleVariantSelection(
                                  product.id,
                                  selectedVariant,
                                );
                              }
                            }}
                            style={{
                              width: '100%',
                              padding: '8px 12px',
                              border: '1px solid #e5e7eb',
                              borderRadius: '6px',
                              fontSize: '14px',
                              marginTop: '8px',
                              backgroundColor: '#ffffff',
                              color: '#374151',
                              outline: 'none',
                            }}
                          >
                            {productVariants[product.id].map((variant) => {
                              // Try to get short variant name from attributes (like "فانيليا", "شوكلاتة")
                              const shortVariantName = variant.attributes?.find(
                                (attr) => attr.name === 'النوع',
                              )?.value;

                              // Fall back to trimmed full name if no short name available
                              const displayName =
                                shortVariantName ||
                                (variant.name.length > 30
                                  ? `${variant.name.substring(0, 30)}...`
                                  : variant.name);

                              return (
                                <option
                                  key={variant.id}
                                  value={variant.id}
                                  title={variant.name} // Full name on hover
                                >
                                  {displayName}
                                </option>
                              );
                            })}
                          </select>
                        </div>
                      )}
                  </div>
                </div>

                {/* Show plus sign between products, but skip for hidden first product */}
                {index < products.length - 1 && !(hideTitle && index === 0) && (
                  <div className="frequently-bought-together-plus">+</div>
                )}
              </React.Fragment>
            );
          })}
        </div>

        <div className="frequently-bought-together-equals">=</div>

        <div className="frequently-bought-together-total">
          <div className="frequently-bought-together-total-label">
            {language === 'ar' ? 'المجموع' : 'Total'}
          </div>

          <div className="frequently-bought-together-total-price">
            {hasDiscount ? (
              <div className="price-container">
                <div className="current-price">
                  <span className="price-value">
                    {formatNumbers(
                      total,
                      language === 'ar' ? 'ar-SA' : 'en-US',
                      currency,
                    )}
                  </span>
                  {currency === 'SAR' && <RIYALIcon className="riyal-icon" />}
                </div>
                <div className="original-price">
                  <span className="price-value">
                    {formatNumbers(
                      originalTotal,
                      language === 'ar' ? 'ar-SA' : 'en-US',
                      currency,
                    )}
                  </span>
                  {currency === 'SAR' && <RIYALIcon className="riyal-icon" />}
                </div>
              </div>
            ) : (
              <div className="current-price">
                <span className="price-value">
                  {formatNumbers(
                    total,
                    language === 'ar' ? 'ar-SA' : 'en-US',
                    currency,
                  )}
                </span>
                {currency === 'SAR' && <RIYALIcon className="riyal-icon" />}
              </div>
            )}
          </div>

          <button
            className="frequently-bought-together-add-all-btn"
            onClick={() => onAddAllToCart(selectedProductsArray)}
            disabled={isLoading || selectedProducts.size === 0}
            style={{
              backgroundColor: primaryColor,
              borderColor: primaryColor,
              opacity: selectedProducts.size === 0 ? 0.5 : 1,
            }}
          >
            {isLoading ? (
              <span className="loader-alfinder-add-to-cart"></span>
            ) : (
              `${
                language === 'ar'
                  ? `إضافة ${selectedProducts.size} منتج للسلة`
                  : `Add ${selectedProducts.size} products to cart`
              }`
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default FrequentlyBoughtTogether;
